import { FaroTable } from "@components/common/faro-table/faro-table";
import {
  MemberBulkButtonComponents,
  MemberTableSubject,
  TeamMemberBulkButtonComponents,
} from "@components/common/faro-table/faro-table-types";
import { getMembersColumns } from "@components/table/members/members-column/members-columns";
import { getMemberTableTrackingSubject, MemberHeaders } from "@components/table/members/members-table-utils";
import { BaseCompanyIdProps, UserId } from "@custom-types/base-types";
import { BaseMembersProps } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useDateTime } from "@hooks/use-date-time";
import { useMediaQueryList } from "@hooks/use-media-query";
import { SxProps, Theme } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ReactNode, useCallback, useEffect } from "react";

interface Props extends Partial<BaseCompanyIdProps>, BaseMembersProps {
  /** The selected project if there is one */
  project?: SdbProject | null;

  /** Recognizes on which context the table is being used */
  tableSubject: MemberTableSubject;

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: MemberHeaders[];

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?:
    | MemberBulkButtonComponents
    | TeamMemberBulkButtonComponents;

  /** The selected team if there is one */
  team?: FaroCoreApiTypes.Team;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** The component that will render for the side panel  */
  sidePanelContent?: ReactNode;

  /** The size of the side panel  */
  sidePanelContentWidth?: string;

  /** The status of the side panel  */
  isSidePanelOpened?: boolean;

  /** Callback fired when a row is clicked. */
  onRowClick?: (id: UserId) => void;

  /** Customize css */
  sx?: SxProps<Theme>;
}

/** Listing all the members of a subject such as a company or a project in a table */
export function MembersTable({
  companyId,
  members,
  project,
  isLoading,
  tableSubject,
  group,
  requiredColumns,
  bulkActionButtons,
  team,
  shouldHideCheckbox,
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  onRowClick,
  sx,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const tableType = useAppSelector(tableTypeSelector);
  const { trackEvent } = useTrackEvent();

  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();

  const { formatDate } = useDateTime();

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    /**
     * Reset the table state when the component is mounted
     * Previously, the table state was reset on unmount via the return function inside useEffect.
     * However, this approach caused issues when the "members" list was empty. When there were no members,
     * the table component would unmount, triggering the resetTableState action. This would reset the state,
     * which caused issues with other parts of the UI, such as showing the "empty content" message or causing
     * errors related to unmounted components.
     *
     * By moving the state reset to the start of the component lifecycle, we ensure that the table state is
     * correctly reset whenever a member-related table is mounted. This ensures consistency in state handling
     * without triggering unintended resets during unmounting.
     */
    dispatch(resetTableState());

    switch (tableSubject) {
      case "workspace":
        dispatch(setTableType(TableType.companyMembers));
        break;

      case "project":
        dispatch(setTableType(TableType.projectMembers));
        break;

      case "group":
        dispatch(setTableType(TableType.groupMembers));
        break;

      case "team":
        dispatch(setTableType(TableType.teamMembers));
        break;

      case "activeMembers":
        dispatch(setTableType(TableType.activeMembers));
        break;
    }
  }, [tableSubject, dispatch]);

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const sortItem = newModel?.[0];
      // Track the sort event
      trackEvent({
        name: TableEvents.sortItems,
        props: {
          subject: getMemberTableTrackingSubject(tableSubject),
          sortBy: sortItem?.field ?? undefined,
          sortOrder: sortItem?.sort ?? "undefined",
          viewMode: "list",
        },
      });
    },
    [tableSubject, trackEvent]
  );

  return (
    <FaroTable
      entities={members}
      uniqueIdKey={"identity"}
      isLoading={isLoading}
      columns={requiredColumns.map(
        (requiredColumn) =>
          getMembersColumns({
            companyId: companyId ?? "",
            project,
            tableSubject,
            group,
            team,
            formatDate,
            options: {
              isScreenLgOrLarger,
              isScreenMd,
            },
          })[requiredColumn]
      )}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={shouldHideCheckbox}
      sidePanelContent={sidePanelContent}
      sidePanelContentWidth={sidePanelContentWidth}
      isSidePanelOpened={isSidePanelOpened}
      tableType={tableType ?? TableType.companyMembers}
      onRowClick={onRowClick}
      defaultSortModel={[
        { field: "User", sort: "asc" },
        { field: "Name", sort: "asc" },
      ]}
      onSortModelChange={handleSortModelChange}
      sx={sx}
    />
  );
}
